// src/views/payment/stripe/AddCardForm.js

import React, { useState, useEffect } from "react";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Button, Spin, Alert } from "antd";
import  {controller}  from "../stripe/controller"

// The CheckoutForm remains exactly the same as the previous answer.
// It handles the submission logic.
const CheckoutForm = ({ onSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setErrorMessage(null);

    const { error, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/setup-complete`,
      },
      redirect: "if_required",
    });

    if (error) {
      setErrorMessage(error.message);
    } else if (setupIntent.status === 'succeeded') {
      console.log("Card setup successful!");
      if (onSuccess) onSuccess();
    } else {
      setErrorMessage(`Card setup failed: ${setupIntent.status}`);
    }

    setIsProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      {errorMessage && <Alert message={errorMessage} type="error" style={{ marginBottom: 16 }} showIcon />}
      <PaymentElement />
      <Button
        type="primary"
        htmlType="submit"
        disabled={!stripe || isProcessing}
        loading={isProcessing}
        style={{ marginTop: 24, width: "100%" }}
      >
        {isProcessing ? "Saving..." : "Save Card"}
      </Button>
    </form>
  );
};


/**
 * Main wrapper component.
 * It now fetches Stripe credentials and initializes Stripe dynamically.
 * props:
 * - clientSecret: The client secret from your SetupIntent
 * - onSuccess: () => void - Callback for successful card setup
 */
export default function AddCardForm({ clientSecret, onSuccess }) {
  // State to hold the dynamically loaded Stripe promise
  const [stripePromise, setStripePromise] = useState(null);

  // This useEffect will run once to fetch your Stripe credentials
  useEffect(() => {
    const getStripeCredentials = async () => {
      try {
        const response = await controller.getStripeCredentials();
        const publishableKey = response.publishable_key;
        const accountId = response.account_id;

        if (publishableKey && accountId) {
          // ** THIS IS THE CRITICAL PART FOR STRIPE CONNECT **
          // Initialize loadStripe with the key AND the connected account ID
          setStripePromise(loadStripe(publishableKey, {
            stripeAccount: accountId,
          }));
        } else {
            console.error("Stripe credentials (key or account ID) are missing.");
        }
      } catch (error) {
        console.error("Failed to fetch Stripe credentials:", error);
      }
    };

    getStripeCredentials();
  }, []); // Empty dependency array means this runs only once on mount


  // The component is ready only when both stripePromise and clientSecret are available
  const isReady = stripePromise && clientSecret;
  
  if (!isReady) {
    return <div style={{ textAlign: 'center', padding: '40px 0' }}><Spin tip="Initializing..." /></div>;
  }

  const options = {
    clientSecret,
    appearance: {
      theme: 'stripe',
    },
  };

  return (
    <Elements options={options} stripe={stripePromise}>
      <CheckoutForm onSuccess={onSuccess} />
    </Elements>
  );
}